/**
 * BiometricEnrollmentModal — prompts users to enable Face ID / Touch ID / fingerprint
 * after they've completed onboarding and are using the app.
 *
 * Shown once per user device. Dismissal is permanent (can still enable via Settings).
 * Renders at root layout level, appears on top of tabs when conditions are met.
 */
import { useState, useEffect, useCallback } from 'react'
import { Modal, View, Text, Pressable, StyleSheet, ActivityIndicator, Platform } from 'react-native'
import { Fingerprint, ScanFace, X } from 'lucide-react-native'
import { useAuth } from '../auth/AuthProvider'
import {
  isBiometricsAvailable,
  isBiometricsEnabled,
  hasBeenPromptedForBiometric,
  markBiometricPrompted,
  authenticateWithBiometrics,
  setBiometricsEnabled,
  getBiometricType,
  getBiometricLabel,
} from '../auth/biometrics'
import { toast } from './Toast'
import { events } from '../lib/posthog'
import { haptics } from '../utils/haptics'
import { colors, spacing, borderRadius, typography, shadows, fontFamily } from '../theme'

export default function BiometricEnrollmentModal() {
  const { user, isAuthenticated } = useAuth()
  const [visible, setVisible] = useState(false)
  const [loading, setLoading] = useState(false)
  const [bioType, setBioType] = useState<'face' | 'fingerprint' | 'iris' | null>(null)
  const [bioLabel, setBioLabel] = useState('Biometrisk login')
  const [checked, setChecked] = useState(false)

  // Check conditions on mount and when auth state changes
  useEffect(() => {
    // Only show for authenticated, onboarded users on native
    if (Platform.OS === 'web') return
    if (!isAuthenticated || !user || !user.onboarding_completed) return
    if (checked) return

    ;(async () => {
      try {
        const available = await isBiometricsAvailable()
        if (!available) {
          setChecked(true)
          return
        }
        const enabled = await isBiometricsEnabled()
        if (enabled) {
          setChecked(true)
          return
        }
        const prompted = await hasBeenPromptedForBiometric()
        if (prompted) {
          setChecked(true)
          return
        }

        // All conditions met — show modal
        const type = await getBiometricType()
        setBioType(type)
        setBioLabel(getBiometricLabel(type))
        events.biometricPrompted(type || 'unknown')
        setVisible(true)
        setChecked(true)
      } catch {
        setChecked(true)
      }
    })()
  }, [isAuthenticated, user?.onboarding_completed, checked])

  const handleActivate = useCallback(async () => {
    if (loading) return
    setLoading(true)
    haptics.medium()
    try {
      const success = await authenticateWithBiometrics()
      if (success) {
        await setBiometricsEnabled(true)
        await markBiometricPrompted()
        events.biometricAccepted(bioType || 'unknown')
        toast.success(`${bioLabel} aktiveret`)
        setVisible(false)
      } else {
        // OS-level deny or cancel
        await markBiometricPrompted()
        events.biometricDeclined(bioType || 'unknown', 'os_deny')
        toast.success(`Du kan aktivere ${bioLabel} senere i Indstillinger`)
        setVisible(false)
      }
    } catch {
      await markBiometricPrompted()
      events.biometricDeclined(bioType || 'unknown', 'error')
      setVisible(false)
    } finally {
      setLoading(false)
    }
  }, [bioType, bioLabel, loading])

  const handleSkip = useCallback(async () => {
    await markBiometricPrompted()
    events.biometricDeclined(bioType || 'unknown', 'user_skip')
    setVisible(false)
  }, [bioType])

  if (!visible) return null

  const BioIcon = bioType === 'face' ? ScanFace : Fingerprint

  return (
    <Modal visible transparent animationType="fade" statusBarTranslucent>
      <View style={styles.overlay}>
        <View style={styles.backdrop} />
        <View style={styles.modal}>
          {/* Close button */}
          <Pressable style={styles.closeBtn} onPress={handleSkip} hitSlop={12}>
            <X size={20} color={colors.text.muted} />
          </Pressable>

          {/* Icon */}
          <View style={styles.iconCircle}>
            <BioIcon size={48} color={colors.primary} strokeWidth={1.5} />
          </View>

          {/* Title */}
          <Text style={styles.title}>Log ind hurtigere{'\n'}med {bioLabel}</Text>

          {/* Body */}
          <Text style={styles.body}>
            Spring MitID over næste gang. Brug dit {bioType === 'face' ? 'ansigt' : 'fingeraftryk'} til at åbne appen på et sekund.
          </Text>

          {/* Primary CTA */}
          <Pressable
            style={[styles.primaryBtn, loading && styles.btnDisabled]}
            onPress={handleActivate}
            disabled={loading}
          >
            {loading ? (
              <ActivityIndicator color={colors.text.inverse} size="small" />
            ) : (
              <>
                <BioIcon size={20} color={colors.text.inverse} strokeWidth={2} />
                <Text style={styles.primaryBtnText}>Aktiver {bioLabel}</Text>
              </>
            )}
          </Pressable>

          {/* Secondary */}
          <Pressable style={styles.secondaryBtn} onPress={handleSkip} disabled={loading}>
            <Text style={styles.secondaryBtnText}>Ikke nu</Text>
          </Pressable>
        </View>
      </View>
    </Modal>
  )
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(15, 23, 42, 0.6)',
  },
  modal: {
    backgroundColor: colors.background,
    borderRadius: borderRadius.xl,
    paddingVertical: spacing.xl,
    paddingHorizontal: spacing.lg,
    width: '100%',
    maxWidth: 380,
    alignItems: 'center',
    ...shadows.lg,
  },
  closeBtn: {
    position: 'absolute',
    top: spacing.md,
    right: spacing.md,
    padding: 4,
    zIndex: 1,
  },
  iconCircle: {
    width: 88,
    height: 88,
    borderRadius: 26,
    backgroundColor: colors.primaryLight,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.lg,
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    fontFamily: fontFamily.bold,
    color: colors.text.primary,
    textAlign: 'center',
    letterSpacing: -0.3,
    lineHeight: 30,
  },
  body: {
    fontSize: 15,
    fontFamily: fontFamily.regular,
    color: colors.text.secondary,
    textAlign: 'center',
    lineHeight: 22,
    marginTop: spacing.sm,
    marginBottom: spacing.lg,
    maxWidth: 300,
  },
  primaryBtn: {
    backgroundColor: colors.primary,
    paddingVertical: 14,
    paddingHorizontal: spacing.lg,
    borderRadius: borderRadius.lg,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    minHeight: 52,
    width: '100%',
    ...shadows.colored(colors.primary),
  },
  primaryBtnText: {
    ...typography.bodyBold,
    color: colors.text.inverse,
    fontSize: 16,
  },
  btnDisabled: {
    opacity: 0.6,
  },
  secondaryBtn: {
    paddingVertical: spacing.md,
    marginTop: spacing.xs,
    alignItems: 'center',
    justifyContent: 'center',
  },
  secondaryBtnText: {
    ...typography.body,
    color: colors.text.muted,
    fontSize: 14,
  },
})
